import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import textToMusicPromptImage from 'assets/images/text_to_music.png';
import { truncateFileName } from 'services/helpers/general/truncatedFileName';
import {
  FormulaFields,
  type TextBundle as ITextBundle,
  type UserLoop,
} from 'services/store/ai/types';
import BundleImages from '../BundleImages/BundleImages';
import BundleSongs from '../BundleSongs/BundleSongs';
import CopyTextButton from '../CopyTextButton';
import {
  StyledFormulaTag,
  StyledFormulaTagsContainer,
  StyledPreviewTag,
} from '../../FormulaTags/styled';
import {
  StyledBundleActionSkeleton,
  StyledBundleActions,
  StyledBundleContainer,
  StyledBundleHead,
  StyledBundleInfo,
  StyledBundleTitle,
} from '../styled';
import { PromptText } from './styled';

type TextBundleProps = {
  bundle: ITextBundle;
};

const TextBundle = ({ bundle }: TextBundleProps) => {
  const [ref, inViewport] = useInView();
  const { t } = useTranslation();

  const { prompt, formula, isPreview } = bundle;

  const userLoop = formula.find(
    (item): item is UserLoop => item.field === FormulaFields.UserLoop
  );

  const areTagsShown = isPreview || !!userLoop;

  return (
    <StyledBundleContainer ref={ref}>
      <StyledBundleHead>
        <BundleImages
          isLoading={!inViewport}
          images={[textToMusicPromptImage]}
        />
        <StyledBundleInfo>
          <StyledBundleTitle>
            {t('aiMusicGenerator:textToMusic:promptTitle')}
          </StyledBundleTitle>
          {areTagsShown && (
            <StyledFormulaTagsContainer>
              {isPreview && (
                <StyledPreviewTag
                  label={t('aiMusicGenerator:tagLabels:preview')}
                />
              )}
              {userLoop && (
                <>
                  <StyledFormulaTag
                    label={truncateFileName(userLoop.value.loopFileName)}
                  />
                  <StyledFormulaTag
                    label={`${userLoop.value.entropy * 100} ${t(
                      'aiMusicGenerator:userLoopSelector:entropy'
                    )}`}
                  />
                </>
              )}
            </StyledFormulaTagsContainer>
          )}
          <PromptText>“{prompt}“</PromptText>
          <StyledBundleActions>
            {inViewport ? (
              <CopyTextButton text={prompt} />
            ) : (
              <StyledBundleActionSkeleton />
            )}
          </StyledBundleActions>
        </StyledBundleInfo>
      </StyledBundleHead>

      <BundleSongs bundle={bundle} isLoading={!inViewport} />
    </StyledBundleContainer>
  );
};

export default TextBundle;
